import classNames from 'classnames'

import { EMPTY_LIST } from '../../../../../constants/common'
import { FORMIK_EMPTY_VALUE } from '../../../../../constants/validation'
import { formatNumberWithSeparators } from '../../../../../utils/number'
import {
  ENTITY_PROFILE_HEADER_INFO_ITEM_ACTIONS_KEY_ADD,
  ENTITY_PROFILE_HEADER_INFO_ITEM_ACTIONS_KEY_EDIT,
  EntityProfileHeaderInfoItemText,
} from '../../../../Entity'
import {
  DOC_FLOW_FORM_FIELD_APPLY_FINAL_NAME_CHECKBOX,
  DOC_FLOW_FORM_FIELD_FINAL_DOCUMENT_TITLE,
  DocFlowProfileHeaderInfoItemsFields,
} from '../../../DocFlow.constants'
import { isDocFlowPhaseCombining } from '../../../DocFlow.utils.phases'
import { DocFlowProfileHeaderConnectedRequests } from '../../DocFlowProfileHeaderConnectedRequests'

import {
  DOC_FLOW_PROFILE_HEADER_INFO_ITEM_ACTION_QA_NAMES,
  DOC_FLOW_PROFILE_HEADER_INFO_ITEM_CONNECTED_REQUESTS_ACTIONS,
  DOC_FLOW_PROFILE_HEADER_INFO_ITEM_UK_CREDITS_ACTIONS,
} from './useDocFlowProfileHeaderInfoItems.constants'

import * as styles from './useDocFlowProfileHeaderInfoItems.module.css'

function getDocFlowProfileHeaderInfoItemActionsProps({
  itemActionsProps,
  name,
}) {
  const qaNames = DOC_FLOW_PROFILE_HEADER_INFO_ITEM_ACTION_QA_NAMES[name]

  return qaNames
    ? {
        buttonsProps: {
          [ENTITY_PROFILE_HEADER_INFO_ITEM_ACTIONS_KEY_ADD]: {
            qaName: qaNames.add,
          },
          [ENTITY_PROFILE_HEADER_INFO_ITEM_ACTIONS_KEY_EDIT]: {
            qaName: qaNames.edit,
          },
        },
      }
    : itemActionsProps
}

function isDocFlowProfileHeaderInfoItemBuildingScope(name) {
  return (
    name === DocFlowProfileHeaderInfoItemsFields.Buildings ||
    name === DocFlowProfileHeaderInfoItemsFields.BuildingGroups
  )
}

function getDocFlowProfileHeaderInfoItemBuildingScopeValue(item) {
  return item?.label?.title ?? null
}

function isDocFlowProfileHeaderInfoItemEditable({ data, disableEdit }) {
  return !data?.archived && !disableEdit
}

function getDocFlowProfileHeaderInfoItemBuildingScope({
  data,
  disableEdit,
  name,
}) {
  const canEdit = isDocFlowProfileHeaderInfoItemEditable({ data, disableEdit })

  return {
    actions: canEdit ? undefined : null,
    canEdit,
    name,
    value: getDocFlowProfileHeaderInfoItemBuildingScopeValue(data?.[name]),
  }
}

function getDocFlowProfileHeaderInfoItemConnectedRequests({
  data,
  disableEdit,
  onRemoveConnectedRequest,
}) {
  const canEdit = !disableEdit

  return {
    actions: canEdit
      ? DOC_FLOW_PROFILE_HEADER_INFO_ITEM_CONNECTED_REQUESTS_ACTIONS
      : null,
    canEdit,
    name: DocFlowProfileHeaderInfoItemsFields.ConnectedRequests,
    noDataText: 'DocFlow.ProfileInfoItemNoConnectedRequestsTitleKey',
    title: 'DocFlow.ProfileInfoItemConnectedRequestsTitleKey',
    value:
      data?.[DocFlowProfileHeaderInfoItemsFields.ConnectedRequests] ??
      EMPTY_LIST,
    onRemoveRequest: canEdit ? onRemoveConnectedRequest : undefined,
  }
}

export function isDocFlowProfileHeaderInfoItemFinalDocumentTitle(name) {
  return name === DocFlowProfileHeaderInfoItemsFields.FinalDocumentTitle
}

export function renderDocFlowProfileHeaderInfoItem({
  canEdit,
  itemActionsProps,
  name,
  value,
  ...restProps
}) {
  const itemBaseProps = {
    canEdit,
    contentWrapperClassName: classNames(styles.contentWrapper, {
      [styles.editableContentWrapper]:
        canEdit &&
        (isDocFlowProfileHeaderInfoItemFinalDocumentTitle(name) ||
          isDocFlowProfileHeaderInfoItemBuildingScope(name)),
    }),
    itemActionsProps: getDocFlowProfileHeaderInfoItemActionsProps({
      itemActionsProps,
      name,
    }),
    titleClassName: styles.itemTitle,
  }

  switch (name) {
    case DocFlowProfileHeaderInfoItemsFields.BuildingGroups:
    case DocFlowProfileHeaderInfoItemsFields.Buildings:
    case DocFlowProfileHeaderInfoItemsFields.FinalDocumentTitle:
    case DocFlowProfileHeaderInfoItemsFields.UploadedDocuments:
      return (
        <EntityProfileHeaderInfoItemText
          {...restProps}
          {...itemBaseProps}
          name={name}
          value={value}
        />
      )

    case DocFlowProfileHeaderInfoItemsFields.EpostSpentCredits:
    case DocFlowProfileHeaderInfoItemsFields.UkEpostCredits:
      return (
        <EntityProfileHeaderInfoItemText
          {...restProps}
          {...itemBaseProps}
          name={name}
          value={formatNumberWithSeparators(value)}
        />
      )

    case DocFlowProfileHeaderInfoItemsFields.ConnectedRequests:
      return (
        <DocFlowProfileHeaderConnectedRequests
          {...restProps}
          {...itemBaseProps}
          name={name}
          value={value}
        />
      )

    default:
      return null
  }
}

export function getPreparedDocFlowProfileHeaderInfoItems({
  data,
  disableEdit,
  isResultPhase,
  onRemoveConnectedRequest,
}) {
  return [
    {
      canEdit:
        data?.permissions.can_edit_final_document_template_name && !disableEdit,
      name: DocFlowProfileHeaderInfoItemsFields.FinalDocumentTitle,
      title: 'DocumentStapling.ProfileInfoItemFinalDocumentTitleKey',
      value: data?.[DocFlowProfileHeaderInfoItemsFields.FinalDocumentTitle],
    },
    {
      ...getDocFlowProfileHeaderInfoItemBuildingScope({
        data,
        disableEdit,
        name: DocFlowProfileHeaderInfoItemsFields.BuildingGroups,
      }),
      noDataText: 'DocFlow.ProfileInfoItemNoBuildingGroupsTitleKey',
      title: 'DocFlow.ProfileInfoItemBuildingGroupsTitleKey',
    },
    {
      ...getDocFlowProfileHeaderInfoItemBuildingScope({
        data,
        disableEdit,
        name: DocFlowProfileHeaderInfoItemsFields.Buildings,
      }),
      noDataText: 'DocFlow.ProfileInfoItemNoBuildingsTitleKey',
      title: 'DocFlow.ProfileInfoItemBuildingsTitleKey',
    },
    {
      actions: null,
      canEdit: false,
      name: DocFlowProfileHeaderInfoItemsFields.UploadedDocuments,
      noDataText: 'DocumentStapling.ProfileInfoItemNoUploadedDocumentsTitleKey',
      title: 'DocumentStapling.ProfileInfoItemUploadedDocumentsTitleKey',
      value: data?.[DocFlowProfileHeaderInfoItemsFields.UploadedDocuments],
    },
    isResultPhase
      ? {
          actions: null,
          canEdit: false,
          name: DocFlowProfileHeaderInfoItemsFields.EpostSpentCredits,
          title: 'DocFlow.ProfileInfoItemSpentCreditsTitleKey',
          value: String(
            data?.[DocFlowProfileHeaderInfoItemsFields.EpostSpentCredits] ?? 0
          ),
        }
      : {
          actions: DOC_FLOW_PROFILE_HEADER_INFO_ITEM_UK_CREDITS_ACTIONS,
          canEdit: true,
          name: DocFlowProfileHeaderInfoItemsFields.UkEpostCredits,
          title: 'DocFlow.ProfileInfoItemAvailableCreditsTitleKey',
          value: String(
            data?.[DocFlowProfileHeaderInfoItemsFields.UkEpostCredits] ?? 0
          ),
        },
    getDocFlowProfileHeaderInfoItemConnectedRequests({
      data,
      disableEdit,
      onRemoveConnectedRequest,
    }),
  ]
}

export function getDocFlowProfileHeaderInfoItemsEditFinalDocTitlePayload(
  values
) {
  const payload = {
    [DOC_FLOW_FORM_FIELD_FINAL_DOCUMENT_TITLE]:
      values[DOC_FLOW_FORM_FIELD_FINAL_DOCUMENT_TITLE],
  }

  if (DOC_FLOW_FORM_FIELD_APPLY_FINAL_NAME_CHECKBOX in values) {
    payload[DOC_FLOW_FORM_FIELD_APPLY_FINAL_NAME_CHECKBOX] =
      values[DOC_FLOW_FORM_FIELD_APPLY_FINAL_NAME_CHECKBOX]
  }

  return payload
}

export function getDocFlowProfileHeaderInfoItemFinalNameInitialValues({
  phase,
  value,
}) {
  const values = {
    [DocFlowProfileHeaderInfoItemsFields.FinalDocumentTitle]: value,
    [DocFlowProfileHeaderInfoItemsFields.Variable]: FORMIK_EMPTY_VALUE,
  }

  if (isDocFlowPhaseCombining(phase)) {
    values[DOC_FLOW_FORM_FIELD_APPLY_FINAL_NAME_CHECKBOX] = false
  }

  return values
}
